import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { User, Note } from "../apis/armoryApi";
import { useArmory, useArmoryDispatch } from "../contexts/ArmoryContext";
import { useArmoryApi } from "../hooks/useArmoryApi";

interface NotesPanelProps {
	initialNotes?: Note[];
	inventoryObjectId?: number;
}

const NotesPanel: React.FC<NotesPanelProps> = ({
	initialNotes = [],
	inventoryObjectId = 0,
}) => {
	const [notes, setNotes] = useState<Note[]>(initialNotes);
	const [newNote, setNewNote] = useState<string>("");
	const [currentUser, setCurrentUser] = useState<User>();
	const { userGid } = useArmory();
	const { getUserById, addAssetNote, getAssetNotes } = useArmoryApi();
	const dispatch = useArmoryDispatch();

	useEffect(() => {
		async function fetchUser() {
			try {
				const response = await getUserById(userGid);

				if (response.data)
				{
					setCurrentUser(response.data);
				}
				else
				{
					const errorMsg: string = "Returned empty result when fetching user."
					console.error(errorMsg);
					dispatch({type: "SHOW_TOAST", payload: {severity: "error", summary: "Error", detail: errorMsg, life: 60000}});
					dispatch({type: "ADD_LOG_ENTRY", payload: {message: errorMsg}});
				}
			} catch (e) {
				const errorMsg: string = `Failed to fetch current user: ${e}`
				console.error(errorMsg);
				dispatch({type: "SHOW_TOAST", payload: {severity: "error", summary: "Error", detail: errorMsg, life: 60000}});
				dispatch({type: "ADD_LOG_ENTRY", payload: {message: errorMsg}});
			}
		}

		fetchUser();
	}, [userGid]);

	useEffect(() => {
		async function getInitialNotes() {
			try {
				const response = await getAssetNotes(inventoryObjectId);

				if (response.data)
				{
					setNotes(response.data);
				}
			} catch (e) {
				const errorMsg: string = `Failed to fetch notes: ${e}`
				console.error(errorMsg);
				dispatch({type: "SHOW_TOAST", payload: {severity: "error", summary: "Error", detail: errorMsg, life: 60000}});
				dispatch({type: "ADD_LOG_ENTRY", payload: {message: errorMsg}});
			}
		}
		
		getInitialNotes();
	}, [inventoryObjectId]);

	const handleAddNote = (): void => {
		if (!newNote.trim()) {
			return;
		}

		const newEntry: Note = {
			inventoryObjectId,
			text: newNote.trim(),
			submitterName: `${currentUser?.firstName ?? ""} ${currentUser?.preferredName ? "\"" + currentUser.preferredName + "\" " : ""}${currentUser?.lastName ?? ""}`,
			submitterId: userGid,
			timestamp: new Date().toISOString(),
		};

		addAssetNote(newEntry);

		setNotes([newEntry, ...notes]);
		setNewNote("");
	};

	return (
    <div style={{ maxWidth: 500, margin: "0 auto"}}>
      {notes.length === 0 ? (
        <p className="text-center text-500">No notes yet.</p>
      ) : (
        notes.map((note, index) => (
          <Card 
			key={index} 
			className="mb-2 shadow-1" 
			style={{
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
			}}
		  >
            <p style={{ whiteSpace: "pre-wrap", marginBottom: "0.5rem", wordBreak: "break-word", overflowWrap: "break-word" }}>
              {note.text}
            </p>
            <div
              style={{
                fontSize: "0.8rem",
                color: "#6b7280",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>{note.submitterName}</span>
              <span>{new Date(note.timestamp + 'Z').toLocaleString()}</span>
            </div>
          </Card>
        ))
      )}

	  <Card title="Add a Note" className="mb-3 shadow-2">
        <InputTextarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          rows={3}
          placeholder="Write your note..."
          className="w-full mb-2"
          autoResize
        />
        <Button
          label="Add Note"
          icon="pi pi-plus"
          onClick={handleAddNote}
          className="w-full"
        />
      </Card>
    </div>
  );
};

export default NotesPanel;